import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { buildAzureStoredPhotoUrl } from 'apps/account/src/account-shared/pipes/azure-stored-photo.util';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { IdentityService, SessionService } from 'account-hybrid/core/authentication';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { filter, find, forEach, includes, isEmpty, last, map, some } from 'lodash-es';
import { StylingV1ApiService } from 'apps/account/src/account-shared/services/styling-v1-api.service';
import { endpointUrl } from 'apps/account/src/account-shared/services/endpoint-url.util';
import { changesTransformer, transformType } from 'apps/account/src/account-shared/services/changes-transformer.util';
import { VersionSelectionService } from 'apps/account/src/account-shared/services/version-selection.service';
import { languageHelper } from 'apps/account/src/account-shared/services/language-helper.util';
import { FtTasksService } from 'apps/account/src/account-shared/services/ft-tasks.service';
import { ConfirmationImageV1ApiService } from 'apps/account/src/account-shared/services/confirmation-image-v1-api.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CurationBackgroundImageDirective } from '../../stories/curation-card/background-image.directive';
import { FieldEditorModalConfig, FtFieldEditorModalComponent } from '../contest-sheets/field-editor-modal.component';
import { FtPendingChangesPanelComponent } from './ft-pending-changes-panel.component';
import { computeDiffs, revertDiffs, snapshotClone } from '../contest-sheets/native-diff.util';
import { FtImageLibraryComponent } from './ft-image-library.component';
import { FtImageComponent } from '../contest-sheets/ft-image.component';
import { FtEmailImagesPreviewComponent } from '../contest-sheets/ft-email-images-preview.component';
import { EmailBannerSignal, EmailBannerUpdatesService } from './email-banner-updates.service';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtModalChromeComponent } from '../../../common/components/modal/ft-modal-chrome.component';

/**
 * Native port of the AngularJS confirmation-touchpoint STYLING editor. Read-view (per-booking-engine
 * CSS panels: Touchpoint CSS + Parent CSS) rendered verbatim; edit machinery is native (field-editor
 * modal + pending-changes panel tracking `model.bookingEngines`) over the native V1Api services. The
 * `styling` payload + propertyImages are passed in from the container (one Styling.get + Image fetch).
 */
@Component({
    selector: 'ft-touchpoint-styling-ng',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        CurationBackgroundImageDirective,
        FtFieldEditorModalComponent,
        FtPendingChangesPanelComponent,
        FtImageLibraryComponent,
        FtImageComponent,
        FtEmailImagesPreviewComponent,
        FtModalComponent,
        FtModalChromeComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./ft-touchpoint-styling.component.scss'],
    templateUrl: './ft-touchpoint-styling.component.html',
})
export class FtTouchpointStylingComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly styling = input<any>();
  readonly propertyImages = input<any>();
  readonly layerName = input.required<string>();
  readonly touchpointType = input.required<string>();
  readonly touchpointGroupId = input.required<string>();
  readonly propertyUuid = input.required<string>();

  readonly panel = viewChild<FtPendingChangesPanelComponent>('panel');

  // B3: confirmation email-images gallery + SignalR hub (native; replaces the bridged ft-email-images-preview).
  /** The native gallery preview modal open flag (v1 modals.emailImages.confirmation.preview). */
  protected readonly galleryOpen = signal(false);
  /** Per-image desktop/mobile toggle (v1 per-row toolbar.isDesktop, ng-init true). Keyed by the image object. */
  private readonly desktopState = new Map<any, boolean>();
  /** confirmation banner desktop CDN suffix (v1 setupImagePrefix: confirmation → 'large'). */
  protected readonly cdnSuffixForDesktopImage = 'large';
  /** The hub completed$ subscription (unsubscribed in ngOnDestroy). */
  private hubSub?: Subscription;

  /** ftModal phase 2 / WS3: the native field-editor modal request (null = closed). */
  protected readonly fieldEditorConfig = signal<FieldEditorModalConfig | null>(null);
  /** Field-level open-time snapshot of the edited datasource, for the field modal's cancel-revert. */
  private fieldSnapshot: any = null;
  /** The object the open field editor mutates (model.styling or a model.bookingEngines[i]). */
  private fieldDatasource: any = null;

  /** ftModal phase 2 / WS3: the native confirmation-email-image "Banner image" modal open flag. */
  protected readonly imageLibraryModalOpen = signal(false);
  /** Modal-open snapshot of {styling, isUseDefaultImage} for the X/Cancel revert (v1 ft-snapshot). */
  private imageLibrarySnapshot: { styling: any; isUseDefaultImage: any } | null = null;
  /** Stable id for the inline "use default image" .ft-switch toggle (label[for] target). */
  protected readonly bannerSwitchUid = 'ft-styling-banner-default-image-switch';

  /** ftModal phase 2 / WS3: the native BannerSize "dimensions" modal open flag. */
  protected readonly dimensionsModalOpen = signal(false);
  /** Modal-open snapshot of model.styling for the X/Cancel revert (v1 ft-snapshot="model.styling"). */
  private dimensionsSnapshot: any = null;

  // B2: native email-template-picker modals (replace the 5 bridged fieldsEditor.compile <ft-modal>s).
  /** open flags (one per native overlay). */
  protected readonly emailTemplateModalOpen = signal(false);
  protected readonly reminderNonViewModalOpen = signal(false);
  protected readonly reminderAfterViewModalOpen = signal(false);
  protected readonly reengagementNoQuoteModalOpen = signal(false);
  protected readonly reengagementNoPhotoModalOpen = signal(false);
  /** v1 `ft-click-class toggle-class="active"` state for the open modal's dropdown (only one open at a time). */
  protected readonly dropdownActive = signal(false);
  /**
   * Modal-open snapshots of {selected, templates} REFERENCES for the X/Cancel rollback (v1 ft-snapshot).
   * Reference-capture is byte-faithful: the modal only ever reassigns the *SelectedTemplate variable, it
   * never mutates the captured object or the (immutable-inside-the-modal) array — so restoring the original
   * reference reproduces the v1 rollback value exactly, WITHOUT the leaf-level corruption v1's deep-diff
   * `changesTracker.rollback` would inflict on the picked array item (the 2y "corruption ≠ preserve-bug"
   * adjudication; the Done/PUT path is byte-identical regardless). null = no snapshot armed for this modal.
   * followup + the 5 photo/homepage pickers arm NO snapshot (v1 has none); invitation-email + reminder ×2 +
   * reengagement ×2 DO.
   */
  private emailTemplateSnapshot: { selected: any; templates: any } | null = null;
  private reminderNonViewSnapshot: { selected: any; templates: any } | null = null;
  private reminderAfterViewSnapshot: { selected: any; templates: any } | null = null;
  private reengagementNoQuoteSnapshot: { selected: any; templates: any } | null = null;
  private reengagementNoPhotoSnapshot: { selected: any; templates: any } | null = null;

  protected readonly model = signal<any>({ bookingEngines: [] });
  version: any = {};

  /**
   * nav-pane screen state — followup uses 'followup'(default)/'share';
   * reminder-emails uses 'non-view'(default)/'after-view'; reengagement-emails uses
   * 'no-quote'(default)/'no-photo'. Seeded per touchpointType.
   */
  protected readonly screenName = signal<'followup' | 'share' | 'non-view' | 'after-view' | 'no-quote' | 'no-photo' | 'signup' | 'signup-guest' | 'signup-hosted'>('followup');

  /**
   * The nested email-template-picker model (Styling.getEmail). followup/invitation use the
   * single `templates`/`selectedTemplate`; reminder-emails uses the two-list non-view/after-view
   * fields; reengagement-emails uses the two-list no-quote/no-photo fields. All bound to the
   * native ft-modal-overlay dropdown pickers in-template (B2 — no bridge).
   */
  protected readonly emailTemplate = signal<any>({
    templates: [],
    selectedTemplate: {},
    nonViewTemplates: [],
    afterViewTemplates: [],
    nonViewSelectedTemplate: {},
    afterViewSelectedTemplate: {},
    noQuoteTemplates: [],
    noPhotoTemplates: [],
    noQuoteSelectedTemplate: {},
    noPhotoSelectedTemplate: {},
  });

  private multiEngineMode = false;
  private bookingEngines: any[] = [];

  // WS5 #10 Stage B B4b: the 12 AngularJS factories the styling editor grabbed via
  // INJECTOR_TOKEN/$injector are now native — utils are bare module objects (verbatim
  // ports), services are inject()ed twins (mirrors the proven ft-touchpoint-messaging/
  // -integration 2w/2x drains). The v1 '_' service IS this lodash module instance (2p).
  private readonly stylingApi: any = inject(StylingV1ApiService);
  private readonly endpointUrl: any = endpointUrl;
  private readonly changesTransformer: any = changesTransformer;
  private readonly transformType: any = transformType;
  private readonly versionSelection: any = inject(VersionSelectionService);
  private readonly identity: any = inject(IdentityService);
  private readonly spaces: any = inject(SpacesService);
  private readonly session: any = inject(SessionService);
  private readonly languageHelper: any = languageHelper;
  private readonly parse: any = inject(ParseService);
  private readonly ftTasks: any = inject(FtTasksService);
  private readonly confirmationImage: any = inject(ConfirmationImageV1ApiService);
  private stylingSnapshot: any;

  private readonly translateSvc = inject(TranslateService);
  private readonly zone = inject(NgZone);
  private readonly environment = inject<Environment>(ENVIRONMENT_TOKEN);
  private readonly emailBannerUpdates = inject(EmailBannerUpdatesService);

  /** banner-only: the flat touchpointCSS value (drives the css-text + ft-placeholder). */
  protected get bannerCss(): string {
    return (this.model().styling && this.model().styling['touchpointCSS']) || '';
  }

  private get isBanner(): boolean {
    return (
      this.touchpointType() === 'pre-arrival-email' ||
      this.touchpointType() === 'confirmation-email-image' ||
      this.touchpointType() === 'poststay-email-image'
    );
  }

  /**
   * Single email-template-picker touchpoints (StylingEmailTemplateController): invitation-email +
   * the 4 photo-advocacy contest emails. All share the invitation-email styling branch + the single
   * `emailTemplate.templates`/`selectedTemplate` model (Styling.getEmail) + the bridged emailTemplateModal.
   */
  get isEmailTemplatePicker(): boolean {
    return ['invitation-email', 'finalistsEmail', 'reminderEmail', 'activityEmail', 'winnerEmail', 'homepageEmail'].includes(this.touchpointType());
  }

  /** ngSwitch key: collapse all single-picker touchpoints onto the 'invitation-email' branch. */
  get stylingBranch(): string {
    return this.isEmailTemplatePicker ? 'invitation-email' : this.touchpointType();
  }

  ngOnInit(): void {
    // invitation-email styling is JUST the email-template picker — the adapter provides no
    // `styling` payload (Styling.get isn't fetched for it), so skip the model.styling activate()
    // (which does Object.hasOwnProperty.call(styling,...) → throws on undefined).
    if (this.styling()) {
      this.activate();
    }
    if (this.touchpointType() === 'followup' || this.isEmailTemplatePicker) {
      this.loadEmailTemplate();
    }
    // reminder-emails styling = a 2-screen email-template picker (no styling payload, like
    // invitation): open on the non-view screen + load both template lists.
    if (this.touchpointType() === 'reminder-emails') {
      this.screenName.set('non-view');
      this.loadReminderEmailTemplates();
    }
    // reengagement-emails styling = a 2-screen email-template picker (no-quote / no-photo).
    if (this.touchpointType() === 'reengagement-emails') {
      this.screenName.set('no-quote');
      this.loadReengagementEmailTemplates();
    }
    // friend-landing styling = a 3-screen flat-CSS nav-pane (signup default).
    if (this.touchpointType() === 'landing') {
      this.screenName.set('signup');
    }
    // B2: the email-template picker dropdowns' v1 ft-click-class outside-click close (2y recipe).
    document.addEventListener('click', this.documentClick);
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.documentClick);
    // B3: v1 emailImagesPreview.component.js $onDestroy stopReceiving() (1:1 with sheet open/close).
    this.hubSub?.unsubscribe();
    if (this.touchpointType() === 'confirmation-email-image') {
      this.emailBannerUpdates.stopReceiving();
    }
  }

  ngAfterViewInit(): void {
    // ftModal phase 2 / WS3: the deferred-save bar is now the NATIVE <ft-pending-changes-panel-ng>
    // in the template ([live]="panelLiveRoot": model.bookingEngines for multi-engine confirmation,
    // model.styling for flat/banner). It snapshots lazily on first CSS field-open (panel.setupIfNeeded)
    // and the host nudges recompute() on field close — no bridge.compile, no ft-snapshot. The 15 CSS
    // editors open the native ft-field-editor-modal (ACE) and Save dispatches in onPanelSave (commit
    // for flat/multi-engine, saveEmailImageStyling for banners/photo-river). The confirmation-email-
    // image second snapshot root (model.isUseDefaultImage) is folded into model.styling — toggling
    // it mutates model.styling.backgroundImage, so the styling-rooted diff still detects it.

    // banners (pre-arrival-email / poststay-email-image / confirmation-email-image): the BannerSize
    // "dimensions" <ft-modal> is now NATIVE — see the ft-modal-overlay in the template +
    // showDimensionsModal/onDimensionsDone/onDimensionsCancel. No bridged compile (dimensionsModalHtml retired).

    // confirmation-email-image banner: the image-library modal is also NATIVE (see above). Only the
    // B3: the confirmation-email-image preview is now NATIVE — the read-view twin
    // <ft-email-images-preview-ng> + the RESTORED gallery modal live in the template; the SignalR hub
    // is owned here (was the v1 widget's connect-to-hub="true"). v1 emailImagesPreview.component.js:21-34
    // started receiving on a $watch when images were non-empty (anyNotReadyYetImages is HARDCODED true —
    // the some readiness check is commented out), so the only real condition is "languages present".
    // The 'completed' signal → updateBannerImageStatus (the existing host method; was the bridge's
    // on-image-update-received). ngOnDestroy stops receiving (fixes the v1 per-sheet-close hub leak).
    if (this.touchpointType() === 'confirmation-email-image' && this.model().styling?.languages?.length) {
      this.hubSub = this.emailBannerUpdates.completed$.subscribe((signal: EmailBannerSignal) =>
        this.updateBannerImageStatus(signal),
      );
      this.emailBannerUpdates.startReceiving();
    }

    // B2: the 5 email-template-picker <ft-modal>s are now NATIVE (ft-modal-overlay + ft-modal-chrome-ng
    // in the template + the open flags / show* / on*Done / on*Cancel methods below). No bridged compile.
    // B3: the email-images preview/gallery/hub are native too — FieldsEditorBridgeService is no longer
    // referenced by this component (B4d DELETED the service — every editor it bridged is native now).
  }

  // --- read-view helpers ---

  showBookingEnginesPanel(): boolean {
    return this.bookingEngines.length > 1;
  }

  // --- model setup (ported from StylingController.activate) ---

  private activate(): void {
    const styling = this.styling();
    this.multiEngineMode = Object.prototype.hasOwnProperty.call(styling, 'bookingEngines');
    this.prepare(styling);
    this.setupVersionManager(styling);
    const upgrade = styling.upgrade;
    delete styling.upgrade;

    this.sortLanguageData(styling);

    this.model.set({
      campaignUuid: this.touchpointGroupId(),
      upgradeData: upgrade,
      isActive: styling.isActive == '1',
      mainData: styling,
      styling,
      defaultBackgroundImageUrl: this.session.getProperty(this.spaces.getPropertyUuid()).BackgroundImageUrl,
      propertyImages: this.parse.arrayOrDefault(this.propertyImages().asset_images),
      propertyId: this.spaces.getPropertyUuid(),
      isUseDefaultImage: !styling.backgroundImage,
      bookingEngines: [],
    });

    this.model().styling = this.version.currentIsNewVersion() ? this.model().upgradeData : this.model().mainData;

    if (this.multiEngineMode) {
      this.model().bookingEngines = this.bookingEngines = styling.bookingEngines.item;
      this.model().showEngineId = this.bookingEngines[0] && this.bookingEngines[0].id;
    }

    this.multiEngineMode ? forEach(this.bookingEngines, (e: any) => this.normalizeStrings(e)) : this.normalizeStrings(styling);
  }

  private sortLanguageData(styling: any): void {
    if (styling.languages) {
      this.languageHelper.sortByDefaultLanguage(styling.languages);
      styling.languages = this.parse.arrayOrDefault(styling.languages);
    }
  }

  private normalizeStrings(source: any): void {
    const intProperties = ['desktopWidth', 'mobileWidth'];
    const jsonProperties = ['jsonSettings'];
    const boolProperties = ['generationIsActive'];
    forEach(source, (value: any, key: string) => {
      if (includes(intProperties, key)) {
        source[key] = this.parse.intOrDefault(value);
      } else if (includes(jsonProperties, key)) {
        source[key] = value.replace(/[\r]/g, '');
      } else if (includes(boolProperties, key)) {
        source[key] = this.parse.bool(value);
      } else if (typeof value === 'string') { // v1 angular.isString
        source[key] = value.replace(/[\r]/g, '');
      }
    });
  }

  private prepare(styling: any): void {
    styling.installedVersionId && (styling.installedVersionId = this.parse.intOrDefault(styling.installedVersionId));
    styling.poststayVersionId && (styling.poststayVersionId = this.parse.intOrDefault(styling.poststayVersionId));
    styling.lastVersionId && (styling.lastVersionId = this.parse.intOrDefault(styling.lastVersionId));
  }

  private setupVersionManager(objectToUpgrade: any): void {
    this.version = {
      currentVersion: this.versionSelection.get(this.version && this.version.currentVersion, objectToUpgrade.poststayVersionId, objectToUpgrade.installedVersionId),
      activatedVersion: objectToUpgrade.poststayVersionId,
      updateInstalled: objectToUpgrade.upgrade !== undefined, // v1 angular.isDefined
      availableVersion: objectToUpgrade.lastVersionId,
      currentIsNewVersion: () => this.version && this.version.updateInstalled && this.version.currentVersion === this.version.availableVersion,
    };
  }

  // --- edit-modal bridges (hidden, click-only — verified by live diag) ---

  editTouchpointCss(i: number): void {
    this.openCssField(this.model().bookingEngines[i], 'advancedCss', 'Touchpoint CSS');
  }

  editParentCss(i: number): void {
    this.openCssField(this.model().bookingEngines[i], 'advancedParentCss', 'Parent CSS');
  }

  // --- followup: the email-template picker + the flat CSS panel ---

  /** StylingEmailTemplateController init (styling.email-template.controller.js:14-24). */
  private loadEmailTemplate(): void {
    Promise.resolve(
      this.stylingApi.getEmail({
        propertyUuid: this.spaces.getPropertyUuid(),
        layerName: this.layerName(),
        touchpointUUID: this.touchpointGroupId(),
        touchpointType: this.touchpointType(),
      }),
    ).then((data: any) => {
      this.zone.run(() => {
        this.emailTemplate().templates = data.templates.item;
        const defaultTemplate = find(this.emailTemplate().templates, { isDefaultTemplate: '1' });
        defaultTemplate && (defaultTemplate.name = defaultTemplate.name + ' (Default)');
        this.emailTemplate().selectedTemplate = find(this.emailTemplate().templates, { id: data.templateUUID }) || defaultTemplate;
      });
    });
  }

  // B2: v1 ft-click-class toggle-class="active" (2y create-contest twin) — toggle on inside click
  // (an <li> select bubbles here, closing the menu like v1), close on outside click via documentClick.
  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.dropdownActive.set(!this.dropdownActive());
  }

  private readonly documentClick = (): void => {
    if (this.dropdownActive()) {
      this.dropdownActive.set(false);
    }
  };

  // B2: single email-template picker (followup + the 6 isEmailTemplatePicker types). v1 default ftModal
  // chrome — Done = updateEmailTemplate (PUT) + close, X/Cancel = close (+ rollback for invitation-email
  // only, which alone carried ft-snapshot in v1). The other 6 arm no snapshot → cancel keeps the pick.
  showEmailTemplateModal(): void {
    if (this.emailTemplate().templates && this.emailTemplate().templates.length > 1) {
      this.emailTemplateSnapshot =
        this.touchpointType() === 'invitation-email'
          ? { selected: this.emailTemplate().selectedTemplate, templates: this.emailTemplate().templates }
          : null;
      this.dropdownActive.set(false);
      this.emailTemplateModalOpen.set(true);
    }
  }

  onEmailTemplateDone(): void {
    this.updateEmailTemplate();
    this.closeEmailTemplateModal();
  }

  onEmailTemplateCancel(): void {
    if (this.emailTemplateSnapshot) {
      this.emailTemplate().selectedTemplate = this.emailTemplateSnapshot.selected;
      this.emailTemplate().templates = this.emailTemplateSnapshot.templates;
    }
    this.closeEmailTemplateModal();
  }

  private closeEmailTemplateModal(): void {
    this.emailTemplateModalOpen.set(false);
    this.emailTemplateSnapshot = null;
    this.dropdownActive.set(false);
  }

  /** on-done of the emailTemplateModal — PUTs the selected template (mutating; never auto-triggered). */
  updateEmailTemplate(): void {
    this.stylingApi.putEmail({
      propertyUuid: this.spaces.getPropertyUuid(),
      layerName: this.layerName(),
      touchpointUUID: this.touchpointGroupId(),
      touchpointType: this.touchpointType(),
      templateUUID: this.emailTemplate().selectedTemplate.isDefaultTemplate === '0' ? this.emailTemplate().selectedTemplate.id : '',
    });
  }

  // --- reminder-emails: the 2-screen (non-view / after-view) email-template picker ---
  //     (styling.reminder-emails-template.controller.js, verbatim).

  private loadReminderEmailTemplates(): void {
    Promise.resolve(
      this.stylingApi.getEmail({
        propertyUuid: this.spaces.getPropertyUuid(),
        layerName: this.layerName(),
        touchpointUUID: this.touchpointGroupId(),
        touchpointType: this.touchpointType(),
      }),
    ).then((data: any) => {
      this.zone.run(() => {
        this.emailTemplate().nonViewTemplates = this.parse.arrayOrDefault(data.nonViewEmailTemplates);
        this.emailTemplate().afterViewTemplates = this.parse.arrayOrDefault(data.afterViewEmailTemplates);
        this.emailTemplate().nonViewSelectedTemplate = this.getReminderSelectedTemplate(this.emailTemplate().nonViewTemplates, data.nonViewEmailtemplateUUID);
        this.emailTemplate().afterViewSelectedTemplate = this.getReminderSelectedTemplate(this.emailTemplate().afterViewTemplates, data.afterViewEmailtemplateUUID);
      });
    });
  }

  private getReminderSelectedTemplate(templates: any[], templateUUID: string): any {
    const defaultTemplate = find(templates, { isDefaultTemplate: '1' });
    defaultTemplate && (defaultTemplate.name = defaultTemplate.name + ' (Default)');
    return find(templates, { id: templateUUID }) || defaultTemplate;
  }

  showReminderNonViewTemplateModal(): void {
    if (this.emailTemplate().nonViewTemplates && this.emailTemplate().nonViewTemplates.length > 1) {
      this.reminderNonViewSnapshot = { selected: this.emailTemplate().nonViewSelectedTemplate, templates: this.emailTemplate().nonViewTemplates };
      this.dropdownActive.set(false);
      this.reminderNonViewModalOpen.set(true);
    }
  }

  showReminderAfterViewTemplateModal(): void {
    if (this.emailTemplate().afterViewTemplates && this.emailTemplate().afterViewTemplates.length > 1) {
      this.reminderAfterViewSnapshot = { selected: this.emailTemplate().afterViewSelectedTemplate, templates: this.emailTemplate().afterViewTemplates };
      this.dropdownActive.set(false);
      this.reminderAfterViewModalOpen.set(true);
    }
  }

  onReminderNonViewDone(): void {
    this.updateReminderEmailTemplate();
    this.closeReminderNonViewModal();
  }

  onReminderNonViewCancel(): void {
    if (this.reminderNonViewSnapshot) {
      this.emailTemplate().nonViewSelectedTemplate = this.reminderNonViewSnapshot.selected;
      this.emailTemplate().nonViewTemplates = this.reminderNonViewSnapshot.templates;
    }
    this.closeReminderNonViewModal();
  }

  private closeReminderNonViewModal(): void {
    this.reminderNonViewModalOpen.set(false);
    this.reminderNonViewSnapshot = null;
    this.dropdownActive.set(false);
  }

  onReminderAfterViewDone(): void {
    this.updateReminderEmailTemplate();
    this.closeReminderAfterViewModal();
  }

  onReminderAfterViewCancel(): void {
    if (this.reminderAfterViewSnapshot) {
      this.emailTemplate().afterViewSelectedTemplate = this.reminderAfterViewSnapshot.selected;
      this.emailTemplate().afterViewTemplates = this.reminderAfterViewSnapshot.templates;
    }
    this.closeReminderAfterViewModal();
  }

  private closeReminderAfterViewModal(): void {
    this.reminderAfterViewModalOpen.set(false);
    this.reminderAfterViewSnapshot = null;
    this.dropdownActive.set(false);
  }

  /** on-done of both reminder modals — PUTs both selected templates (mutating; never auto-triggered). */
  updateReminderEmailTemplate(): void {
    this.stylingApi.putReminderEmails({
      propertyUuid: this.spaces.getPropertyUuid(),
      layerName: this.layerName(),
      touchpointUUID: this.touchpointGroupId(),
      touchpointType: this.touchpointType(),
      nonViewEmailTemplateUUID: this.emailTemplate().nonViewSelectedTemplate.isDefaultTemplate === '0' ? this.emailTemplate().nonViewSelectedTemplate.id : '',
      afterViewEmailTemplateUUID: this.emailTemplate().afterViewSelectedTemplate.isDefaultTemplate === '0' ? this.emailTemplate().afterViewSelectedTemplate.id : '',
    });
  }

  // --- reengagement-emails: the 2-screen (no-quote / no-photo) email-template picker ---
  //     (styling.reengagement-emails-template.controller.js, verbatim).

  /** The rule-warning gate (v1 styling has no `model.messaging` → undefined → warning shows). */
  get reengagementPhotoExplorerActive(): boolean {
    return this.parse.bool(this.model()?.messaging?.isPhotoExplorerActive);
  }

  private loadReengagementEmailTemplates(): void {
    Promise.resolve(
      this.stylingApi.getEmail({
        propertyUuid: this.spaces.getPropertyUuid(),
        layerName: this.layerName(),
        touchpointUUID: this.touchpointGroupId(),
        touchpointType: this.touchpointType(),
      }),
    ).then((data: any) => {
      this.zone.run(() => {
        this.emailTemplate().noQuoteTemplates = this.parse.arrayOrDefault(data.noQuoteEmailTemplates);
        this.emailTemplate().noPhotoTemplates = this.parse.arrayOrDefault(data.noPhotoEmailTemplates);
        this.emailTemplate().noQuoteSelectedTemplate = this.getReminderSelectedTemplate(this.emailTemplate().noQuoteTemplates, data.noQuoteEmailtemplateUUID);
        this.emailTemplate().noPhotoSelectedTemplate = this.getReminderSelectedTemplate(this.emailTemplate().noPhotoTemplates, data.noPhotoEmailtemplateUUID);
      });
    });
  }

  showReengagementNoQuoteTemplateModal(): void {
    if (this.emailTemplate().noQuoteTemplates && this.emailTemplate().noQuoteTemplates.length > 1) {
      this.reengagementNoQuoteSnapshot = { selected: this.emailTemplate().noQuoteSelectedTemplate, templates: this.emailTemplate().noQuoteTemplates };
      this.dropdownActive.set(false);
      this.reengagementNoQuoteModalOpen.set(true);
    }
  }

  // NOTE (D6): v1's no-photo read-view panel targeted the mis-cased id "…editnoPhotoTemplate" so the
  // modal never opened (threw); the WS4 native read-view already calls this method with the correct id,
  // a pre-existing gate-blind fix kept here (2y "corruption ≠ preserve-bug").
  showReengagementNoPhotoTemplateModal(): void {
    if (this.emailTemplate().noPhotoTemplates && this.emailTemplate().noPhotoTemplates.length > 1) {
      this.reengagementNoPhotoSnapshot = { selected: this.emailTemplate().noPhotoSelectedTemplate, templates: this.emailTemplate().noPhotoTemplates };
      this.dropdownActive.set(false);
      this.reengagementNoPhotoModalOpen.set(true);
    }
  }

  onReengagementNoQuoteDone(): void {
    this.updateReengagementEmailTemplate();
    this.closeReengagementNoQuoteModal();
  }

  onReengagementNoQuoteCancel(): void {
    if (this.reengagementNoQuoteSnapshot) {
      this.emailTemplate().noQuoteSelectedTemplate = this.reengagementNoQuoteSnapshot.selected;
      this.emailTemplate().noQuoteTemplates = this.reengagementNoQuoteSnapshot.templates;
    }
    this.closeReengagementNoQuoteModal();
  }

  private closeReengagementNoQuoteModal(): void {
    this.reengagementNoQuoteModalOpen.set(false);
    this.reengagementNoQuoteSnapshot = null;
    this.dropdownActive.set(false);
  }

  onReengagementNoPhotoDone(): void {
    this.updateReengagementEmailTemplate();
    this.closeReengagementNoPhotoModal();
  }

  onReengagementNoPhotoCancel(): void {
    if (this.reengagementNoPhotoSnapshot) {
      this.emailTemplate().noPhotoSelectedTemplate = this.reengagementNoPhotoSnapshot.selected;
      this.emailTemplate().noPhotoTemplates = this.reengagementNoPhotoSnapshot.templates;
    }
    this.closeReengagementNoPhotoModal();
  }

  private closeReengagementNoPhotoModal(): void {
    this.reengagementNoPhotoModalOpen.set(false);
    this.reengagementNoPhotoSnapshot = null;
    this.dropdownActive.set(false);
  }

  /** on-done of both reengagement modals — PUTs both selected templates (mutating; never auto-triggered). */
  updateReengagementEmailTemplate(): void {
    this.stylingApi.putReengagementEmails({
      propertyUuid: this.spaces.getPropertyUuid(),
      layerName: this.layerName(),
      touchpointUUID: this.touchpointGroupId(),
      touchpointType: this.touchpointType(),
      noQuoteEmailTemplateUUID: this.emailTemplate().noQuoteSelectedTemplate.isDefaultTemplate === '0' ? this.emailTemplate().noQuoteSelectedTemplate.id : '',
      noPhotoEmailTemplateUUID: this.emailTemplate().noPhotoSelectedTemplate.isDefaultTemplate === '0' ? this.emailTemplate().noPhotoSelectedTemplate.id : '',
    });
  }

  /** contestPage "Story Explorer" CSS editor (edit-contestPage-styles.html — flat advancedCss). */
  editContestPageCss(): void {
    this.openCssField(this.model().styling, 'advancedCss', this.translateSvc.instant('flipto.account.CSS'));
  }

  /** quote "Post-stay Process" CSS editor (edit-quote-styles-v2.html — flat advancedCss, css editor, lg). */
  editQuoteCss(): void {
    this.openCssField(this.model().styling, 'advancedCss', this.translateSvc.instant('flipto.account.CSS'));
  }

  // --- friend LANDING styling: the 3-screen flat CSS editors (edit-landing-styles.html) ---

  editLandingTouchpointCss(): void {
    this.openCssField(this.model().styling, 'advancedCss', 'Touchpoint CSS');
  }

  editLandingParentCss(): void {
    this.openCssField(this.model().styling, 'advancedParentCss', 'Parent CSS');
  }

  editLandingQuoteCss(): void {
    this.openCssField(this.model().styling, 'advancedQuoteCss', 'Touchpoint CSS');
  }

  editLandingQuoteParentCss(): void {
    this.openCssField(this.model().styling, 'advancedQuoteParentCss', 'Parent page CSS');
  }

  editLandingHostedCss(): void {
    this.openCssField(this.model().styling, 'advancedHostedCss', 'CSS');
  }

  // --- HOMEPAGE "Homepage Component" styling: two flat CSS editors (edit-homepage-styles.html) ---

  editHomepageParentCss(): void {
    this.openCssField(this.model().styling, 'advancedParentCss', this.translateSvc.instant('flipto.account.platform.homepage.touchpoints.CssForPageContent'));
  }

  editHomepageDialogCss(): void {
    this.openCssField(this.model().styling, 'advancedCss', this.translateSvc.instant('flipto.account.platform.homepage.touchpoints.CssForSignUpDialog'));
  }

  // --- QUOTE-LIST "Quotes List" styling: single flat CSS editor on advancedParentCss (edit-list-styles.html) ---

  editListParentCss(): void {
    this.openCssField(this.model().styling, 'advancedParentCss', this.translateSvc.instant('flipto.account.CSS'));
  }

  // --- friend LANDING (mobile) styling: single flat CSS editor (edit-mobile-styles.html) ---

  editMobileCss(): void {
    this.openCssField(this.model().styling, 'advancedCss', 'CSS');
  }

  /** The flat advancedCss editor on the 'share' screen (edit-followup-styles.html:47). */
  editFollowupCss(): void {
    this.openCssField(this.model().styling, 'advancedCss', 'CSS');
  }

  // --- banner (pre-arrival + confirmation-email-image): CSS editor + dimensions/image modals + saveEmailImageStyling ---

  editBannerCss(): void {
    // pre-arrival labels the panel "Touchpoint CSS"; confirmation-email-image labels it "CSS".
    const titleKey = this.touchpointType() === 'pre-arrival-email' ? 'flipto.account.TouchpointCSS' : 'flipto.account.CSS';
    this.openCssField(this.model().styling, 'touchpointCSS', this.translateSvc.instant(titleKey));
  }

  // ftModal phase 2 / WS3: open the native BannerSize "dimensions" modal (replaces ftModal.show).
  // The id param is now inert (each component instance is a single banner touchpointType). Snapshot
  // model.styling for X/Cancel + lazily set up the panel so a width edit is detected as a change.
  showDimensionsModal(_modalId = 'modals.photoadvocacy.prearrival.dimensions'): void {
    this.panel()?.setupIfNeeded();
    this.dimensionsSnapshot = snapshotClone(this.model().styling);
    this.dimensionsModalOpen.set(true);
  }

  /** Dimensions modal Done (v1 default ftModal done = close): nudge the deferred-save panel. */
  onDimensionsDone(): void {
    this.closeDimensionsModal();
    this.panel()?.recompute();
  }

  /** Dimensions modal X/Cancel (v1 ftModal snapshot.rollback): revert model.styling. */
  onDimensionsCancel(): void {
    if (this.dimensionsSnapshot) {
      revertDiffs(this.model().styling, this.dimensionsSnapshot, computeDiffs(this.dimensionsSnapshot, this.model().styling));
    }
    this.closeDimensionsModal();
    this.panel()?.recompute();
  }

  private closeDimensionsModal(): void {
    this.dimensionsModalOpen.set(false);
    this.dimensionsSnapshot = null;
  }

  // ftModal phase 2 / WS3: open the native "Banner image" modal (replaces ftModal.show). Snapshot
  // the v1 ft-snapshot targets (model.styling + model.isUseDefaultImage) for X/Cancel + lazily set
  // up the pending-changes panel so an image selection is detected as a deferred change.
  showImageLibraryModal(): void {
    this.panel()?.setupIfNeeded();
    this.imageLibrarySnapshot = { styling: snapshotClone(this.model().styling), isUseDefaultImage: this.model().isUseDefaultImage };
    this.imageLibraryModalOpen.set(true);
  }

  /** The inline "use default image" toggle (replaces the v1 ft-switch on-change). */
  onImageLibrarySwitchToggle(ev: Event): void {
    this.model().isUseDefaultImage = (ev.target as HTMLInputElement).checked;
    this.onUseBackgroundImage();
  }

  /** Banner-image modal Done (v1 default ftModal done = close): nudge the deferred-save panel. */
  onImageLibraryDone(): void {
    this.closeImageLibraryModal();
    this.panel()?.recompute();
  }

  /** Banner-image modal X/Cancel (v1 ftModal snapshot.rollback): revert styling + isUseDefaultImage. */
  onImageLibraryCancel(): void {
    if (this.imageLibrarySnapshot) {
      revertDiffs(this.model().styling, this.imageLibrarySnapshot.styling, computeDiffs(this.imageLibrarySnapshot.styling, this.model().styling));
      this.model().isUseDefaultImage = this.imageLibrarySnapshot.isUseDefaultImage;
    }
    this.closeImageLibraryModal();
    this.panel()?.recompute();
  }

  private closeImageLibraryModal(): void {
    this.imageLibraryModalOpen.set(false);
    this.imageLibrarySnapshot = null;
  }

  removeMobileWidth(): void {
    delete this.model().styling.mobileWidth;
  }

  // --- confirmation-email-image image/preview handlers (styling.controller.js:170-207) ---

  setupStylingSnapshot(snapshot: any): void {
    this.stylingSnapshot = snapshot;
  }

  updateImage(image: any): void {
    this.model().styling.backgroundImage = image ? image.url : null;
  }

  onUseBackgroundImage(): void {
    if (this.model().isUseDefaultImage) {
      delete this.model().styling.backgroundImage;
    }
  }

  refreshConfirmationImage(language: any): void {
    const imageUUID = language.imageUUID;
    Promise.resolve(this.confirmationImage.refresh(this.touchpointGroupId(), language.code, this.spaces.getPropertyUuid()))
      .then((newImageUUID: any) => {
        language.imageUUID = newImageUUID;
        this.stylingSnapshot && this.stylingSnapshot.retake();
      })
      .finally(() => this.ftTasks.finish('generate-email-image-' + imageUUID + '-' + language.code));
  }

  updateBannerImageStatus(signal: any): void {
    // NOTE (pre-existing): the server emits `contestUuid` but this reads `signal.campaignUuid` (undefined
    // at runtime) → the guard never matches a real signal, so the v1 auto-update path is effectively dead.
    // Preserved verbatim (FROZEN parity); flagged for a possible explicit fix (read signal.contestUuid).
    if (signal.imageType === 'confirmation' && this.model().campaignUuid && this.model().campaignUuid.toLowerCase() === signal.campaignUuid) {
      const imageInfo = find(this.model().styling.languages, (image: any) => image.code === signal.languageCode);
      if (imageInfo) {
        imageInfo.imageUUID = signal.imageUuid;
        imageInfo.isImageProcessed = true;
        this.stylingSnapshot && this.stylingSnapshot.retake();
      }
    }
  }

  // --- B3: native email-images gallery (v1 emailImagesPreview.html:25-82, confirmation only) ---

  /** v1 .ft-panel ft-targets-modal opener, gated by ft-no-targets-modal-if-task-pending="update-email-images". */
  openGallery(): void {
    if (this.ftTasks.isPending('update-email-images')) return;
    this.galleryOpen.set(true);
  }

  closeGallery(): void {
    this.galleryOpen.set(false);
  }

  /** v1 per-row toolbar.isDesktop (ng-init true). Defaults true unless explicitly toggled to mobile. */
  isDesktop(image: any): boolean {
    return this.desktopState.get(image) !== false;
  }

  setDesktop(image: any, value: boolean): void {
    this.desktopState.set(image, value);
  }

  /** v1 image.hasMobile | parse:'bool' (the desktop/mobile switcher gate). */
  hasMobile(image: any): boolean {
    return this.parse.bool(image && image.hasMobile);
  }

  /** v1 task key generate-email-image-{imageUUID}-{code} (the per-image refresh task). */
  generateKey(image: any): string {
    return 'generate-email-image-' + image.imageUUID + '-' + image.code;
  }

  /** v1 button ft-disable-while-task-pending + the loader ft-show-while-task-pending. */
  isImagePending(image: any): boolean {
    return this.ftTasks.isPending(this.generateKey(image));
  }

  /** v1 Refresh: ft-start-task (register) + ng-click vm.onRefresh (refreshConfirmationImage finishes the same key). */
  onGalleryRefresh(image: any): void {
    this.ftTasks.register(this.generateKey(image));
    this.refreshConfirmationImage(image);
  }

  /** v1 image.imageUUID | ftAzureStoredPhoto:size ('' → no suffix). */
  azureUrl(uuid: string, size: string): string | null {
    return buildAzureStoredPhotoUrl(uuid, size ? size : null, this.environment.AzureCdnBaseUrl);
  }

  /** Banner save (styling.controller.js:144-164) — a whole-object PUT (never auto-triggered). */
  saveEmailImageStyling(): void {
    const model = {
      desktopWidth: this.model().styling.desktopWidth,
      mobileWidth: this.model().styling.mobileWidth,
      touchpointCSS: this.model().styling.touchpointCSS,
      backgroundImage: this.model().styling.backgroundImage,
    };
    const url = this.endpointUrl.getStyleUrl(this.layerName(), this.touchpointType(), this.touchpointGroupId(), this.spaces.getPropertyUuid());
    this.ftTasks.register('update-email-images');
    Promise.resolve(this.stylingApi.put(url, model)).then((response: any) => {
      // confirmation-email-image re-normalizes + retakes the snapshot; pre-arrival skips it.
      // WS5 #10 Stage B B4b: native StylingV1ApiService.put resolves the BODY (v1 `Styling.put`
      // = $http.put resolved the envelope, so v1 read `response.data`) → read `response` directly.
      if (this.touchpointType() === 'confirmation-email-image' && response) {
        const data = response;
        this.sortLanguageData(data);
        this.normalizeStrings(data);
        this.model().styling = data;
      }
      this.ftTasks.finish('update-email-images');
    });
  }

  // --- native pending-changes panel + CSS field editors (ftModal phase 2 / WS3) ---

  /** The live model root the native pending-changes panel tracks (multi-engine vs flat styling). */
  get panelLiveRoot(): any {
    return this.multiEngineMode ? this.model().bookingEngines : this.model().styling;
  }

  /**
   * Open a CSS (ACE) field editor on `datasource[field]` through the native ft-field-editor-modal.
   * Snapshots the panel lazily (first field-open) + the field's own datasource for cancel-revert.
   */
  private openCssField(datasource: any, field: string, title: string): void {
    this.panel()?.setupIfNeeded();
    this.fieldDatasource = datasource;
    this.fieldSnapshot = snapshotClone(datasource);
    this.fieldEditorConfig.set({
      type: 'css',
      datasource,
      fields: [field],
      size: 'lg',
      title,
      onCancel: () => this.rollbackField(),
      onClose: () => this.afterFieldClose(),
    });
  }

  /** Field cancel: revert the field's own edit (the panel's deferred snapshot is separate). */
  private rollbackField(): void {
    if (!this.fieldSnapshot || !this.fieldDatasource) return;
    revertDiffs(this.fieldDatasource, this.fieldSnapshot, computeDiffs(this.fieldSnapshot, this.fieldDatasource));
  }

  private afterFieldClose(): void {
    this.fieldSnapshot = null;
    this.fieldDatasource = null;
    this.panel()?.recompute();
  }

  /**
   * Panel Save → the v1 commit (flat model.styling / multi-engine model.bookingEngines) or
   * saveEmailImageStyling (banners + photo-river), then re-snapshot. The native diffs are rooted at
   * the [live] root, which the verbatim v1 commit consumes unchanged: FlatData uses last(path) and
   * BookingEngineData uses path[0]+last(path) — both already satisfied by the model.styling-rooted
   * (['advancedCss']) / bookingEngines-rooted ([i,'advancedCss']) native paths. flattenChange is a
   * no-op on these kind:'E' diffs (corrective only if deep-diff ever emits a nested kind:'A').
   */
  onPanelSave(diffs: any[]): void {
    if (this.isBanner || this.touchpointType() === 'photo-river') {
      this.saveEmailImageStyling();
    } else {
      this.commit((diffs || []).map((c: any) => flattenChange(c)));
    }
    this.panel()?.commit();
  }

  /** Panel Cancel: the panel already reverted the model root; refresh the count. */
  onPanelCancel(): void {
    this.panel()?.recompute();
  }

  // --- save (the FULL union the v1 controller PUTs — bridged commit) ---

  commit(deepDiffChanges: any[]): void {
    const mainChanges = filter(deepDiffChanges, (change: any) => change.path.join('.').indexOf('model.upgradeData') === -1);
    const upgradeChanges = filter(deepDiffChanges, (change: any) => change.path.join('.').indexOf('model.upgradeData') !== -1);
    this.saveData(mainChanges, this.version.activatedVersion);
    this.model().upgradeData && this.saveData(upgradeChanges, this.model().upgradeData.poststayVersionId);
  }

  private saveData(deepDiffChanges: any[], version: any): void {
    if (isEmpty(deepDiffChanges)) return;
    const fixedPathDiffs = this.getFixedPathDiffs(deepDiffChanges);
    const changes = this.multiEngineMode
      ? this.changesTransformer(this.transformType.BookingEngineData, fixedPathDiffs, this.bookingEngines)
      : this.changesTransformer(this.transformType.FlatData, deepDiffChanges, this.model().styling);
    const url = this.endpointUrl.getStyleUrl(this.layerName(), this.touchpointType(), this.touchpointGroupId(), this.spaces.getPropertyUuid(), version);
    this.stylingApi.put(url, changes);
  }

  private getFixedPathDiffs(deepDiffChanges: any[]): any[] {
    return map(deepDiffChanges, (change: any) => {
      const copy = snapshotClone(change); // v1 angular.copy of a plain deep-diff change object
      const path = change.path.join('.');
      const patchesToCrop = ['model.bookingEngines'];
      patchesToCrop.forEach((toCrop: string) => {
        if (path.indexOf(toCrop) !== -1) {
          copy.path.splice(0, toCrop.split('.').length);
        }
      });
      return copy;
    });
  }
}

/**
 * Flatten a deep-diff `kind:'A'` (array) change into a flat index path, so a nested in-place edit
 * reads as `{kind:'E', path:[i,'advancedCss'], …}` for the v1 BookingEngineData/FlatData transformer.
 * For the in-place CSS edits here deep-diff already emits `kind:'E'` (object root → ['advancedCss'];
 * array/bookingEngines root → [i,'advancedCss']) so this is a no-op — it's the defensive guard the
 * cert-email port uses, kept identical in case deep-diff emits a whole-array kind:'A' diff.
 */
function flattenChange(c: any, base: any[] = []): any {
  if (c && c.kind === 'A') {
    return flattenChange(c.item, base.concat(c.path || []).concat([c.index]));
  }
  return Object.assign({}, c, { path: base.concat((c && c.path) || []) });
}
